import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HelpCircle,
  Mail,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  SunMedium,
  CloudSun,
  Heart,
  BellRing,
  Crown,
  MapPin
} from 'lucide-react';
import LegalLayout from '../LegalLayout';

const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  const accentColor = '#0d7377';

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-300"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)'
      }}
    >
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between text-left transition-colors"
        style={{ color: 'var(--text-primary)' }}
      >
        <span className="font-medium pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: accentColor }} />
        ) : (
          <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
        )}
      </button>
      {isOpen && (
        <div
          className="px-6 pb-4"
          style={{ color: 'var(--text-secondary)' }}
        >
          {answer}
        </div>
      )}
    </div>
  );
};

const Support = () => {
  const accentColor = '#0d7377';
  const [openFAQ, setOpenFAQ] = useState(0);

  const faqs = [
    {
      icon: <SunMedium className="w-5 h-5" />,
      question: "How does Clearcast choose the best activity windows?",
      answer: (
        <div className="space-y-2">
          <p>Each hour of the week is scored against the preferences on your activity profile:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Temperature</strong> — how close it is to your ideal, within your min/max</li>
            <li><strong>Wind</strong> — decays as speed approaches your max</li>
            <li><strong>Rain chance</strong> — decays as precipitation probability approaches your threshold</li>
            <li><strong>Humidity</strong> — scored against your comfortable range</li>
            <li><strong>UV</strong> — decays above your tolerance</li>
            <li><strong>Time of day</strong> — multipliers for your preferred morning / afternoon / evening / night slots</li>
          </ul>
          <p className="mt-3">The score is a weighted composite (0–100%). Contiguous hours above 40% become a "window".</p>
          <p className="mt-2 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Tap the Details button on any window to see the per-factor breakdown and sport-specific tips.
          </p>
        </div>
      )
    },
    {
      icon: <MapPin className="w-5 h-5" />,
      question: "Why does Clearcast need my location?",
      answer: (
        <div className="space-y-2">
          <p>Clearcast requests "When In Use" location access at kilometer-level accuracy for one reason only: to fetch an accurate weather forecast through Apple's WeatherKit.</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Your location is sent to Apple's services only, not ours</li>
            <li>Clearcast never stores or transmits your location to any external server</li>
            <li>Reverse geocoding (turning coordinates into a place name) uses Apple's services on-device</li>
          </ul>
          <p className="mt-3">If you deny location access, the app continues to work but shows clearly-labelled sample forecast data until you grant permission or connect to the internet.</p>
        </div>
      )
    },
    {
      icon: <Heart className="w-5 h-5" />,
      question: "How does the Apple Health integration work?",
      answer: (
        <div className="space-y-2">
          <p>Apple Health is an optional Pro feature. When you connect it, Clearcast reads your recent outdoor workouts (running, cycling, hiking, etc.) and generates preference insights like:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>"You do 80% of your runs in the morning — prioritise morning windows?"</li>
            <li>"You never run in midday heat — lower your max temperature?"</li>
            <li>"Weekend runner — consider weekend-focused planning?"</li>
          </ul>
          <p className="mt-3"><strong style={{ color: 'var(--text-primary)' }}>All of this happens on-device.</strong> Your health data never leaves your iPhone. You can apply or dismiss each insight individually.</p>
          <p className="mt-2 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Revoke access at any time via Settings → Privacy &amp; Security → Health → Clearcast.
          </p>
        </div>
      )
    },
    {
      icon: <BellRing className="w-5 h-5" />,
      question: "How do notifications work?",
      answer: (
        <div className="space-y-2">
          <p>Clearcast schedules two kinds of local notifications (no server involved):</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Morning Briefing</strong> (free) — a once-daily summary of your top activity windows for the day, at the time you choose in Settings</li>
            <li><strong>Window Alerts</strong> (Pro) — an alert 30 minutes before each high-scoring window opens, so you don't miss a great morning</li>
          </ul>
          <p className="mt-3">Grant permission during onboarding or later in Settings. Turn Window Alerts on or off from Settings → Notifications.</p>
        </div>
      )
    },
    {
      icon: <CloudSun className="w-5 h-5" />,
      question: "Where does the weather data come from?",
      answer: (
        <div className="space-y-2">
          <p>Clearcast uses Apple's <strong>WeatherKit</strong> framework to fetch hourly forecasts for the next 7 days. The data includes temperature, apparent temperature, humidity, wind speed and direction, precipitation probability and amount, UV index, cloud cover, visibility, pressure, and dew point.</p>
          <p className="mt-3">If WeatherKit is temporarily unavailable, Clearcast displays sample forecast data that is clearly labelled "sample data" in Settings → Weather Data. Don't rely on sample data for real planning.</p>
        </div>
      )
    },
    {
      icon: <Crown className="w-5 h-5" />,
      question: "What's included in Clearcast Pro?",
      answer: (
        <div className="space-y-2">
          <p>Clearcast Pro unlocks:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Unlimited activity profiles</strong> — track everything you do outdoors, not just 2</li>
            <li><strong>7-Day Planner</strong> — the Week tab showing the best day for every activity</li>
            <li><strong>Smart Insights</strong> — Apple Health-based preference learning, on-device</li>
            <li><strong>Window Alerts</strong> — notifications 30 minutes before high-scoring windows open</li>
            <li><strong>Sport Conditions</strong> — ground condition and dew factor analysis for football and cricket</li>
          </ul>
          <p className="mt-3">Monthly and yearly subscription lengths are available. Manage or cancel any time in Settings → Apple ID → Subscriptions.</p>
          <p className="mt-2 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            See <Link to="/apps/clearcast/terms" style={{ color: accentColor }}>Terms &amp; Conditions</Link> for full subscription details.
          </p>
        </div>
      )
    },
    {
      icon: <HelpCircle className="w-5 h-5" />,
      question: "How do I restore a previous purchase?",
      answer: (
        <div className="space-y-2">
          <p>If you previously bought Clearcast Pro and reinstalled the app or switched devices:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open Clearcast</li>
            <li>Go to <strong>Settings → About → Restore Purchases</strong>, or tap <strong>Restore Purchases</strong> on the paywall</li>
            <li>Make sure you're signed in with the same Apple ID you used to purchase</li>
          </ol>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Restoration is instant once your subscription is confirmed by Apple.
          </p>
        </div>
      )
    }
  ];

  return (
    <LegalLayout app="Clearcast" docType="Support" icon={HelpCircle}>
        {/* Contact Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <a
            href="mailto:support@feraltechlabs.co.in"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = accentColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <Mail className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Email Support
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  support@feraltechlabs.co.in
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              We typically respond within 24-48 hours
            </p>
          </a>

          <a
            href="mailto:support@feraltechlabs.co.in?subject=Clearcast%20Bug%20Report"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = accentColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <MessageSquare className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Report a Bug
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Help us improve Clearcast
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              Include your iOS version and steps to reproduce
            </p>
          </a>
        </div>

        {/* FAQ Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onToggle={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>

        {/* App Info */}
        <div
          className="rounded-xl p-6"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <h3 className="font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            App Information
          </h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>App Name:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Clearcast</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Developer:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Feral Techlab</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Platform:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>iOS 18+</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Category:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Weather</span>
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link
            to="/apps/clearcast/privacy"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = accentColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Privacy Policy
          </Link>
          <span style={{ color: 'var(--text-tertiary)' }}>•</span>
          <Link
            to="/apps/clearcast/terms"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = accentColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Terms &amp; Conditions
          </Link>
        </div>
    </LegalLayout>
  );
};

export default Support;
